import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain, LayoutDashboard, Users, FileText, TrendingUp, CalendarDays,
  MessageSquare, BookOpen, ShieldCheck, Settings, LogOut, X,
  ChevronRight, Menu, ExternalLink, Sparkles, Layers, Radio,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { isUserCoach, isUserAdmin } from '../../lib/auth';
import { supabase } from '../../lib/supabase';

interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/si/app/dashboard', icon: LayoutDashboard },
  { label: 'Clients', path: '/si/app/clients', icon: Users },
  { label: 'Session Reports', path: '/si/app/reports', icon: FileText },
  { label: 'Coach Intelligence AI', path: '/si/app/coach-ai', icon: Sparkles },
  { label: 'Custom Lens', path: '/si/app/custom-lens', icon: Layers },
  { label: 'Pattern History', path: '/si/app/patterns', icon: TrendingUp },
  { label: 'Next Session Planner', path: '/si/app/planner', icon: CalendarDays },
  { label: 'Follow-Up Assistant', path: '/si/app/followup', icon: MessageSquare },
  { label: 'Question Bank', path: '/si/app/questions', icon: BookOpen },
  { label: 'Consent & Ethics', path: '/si/app/consent', icon: ShieldCheck },
  { label: 'Settings', path: '/si/app/settings', icon: Settings },
];

const WALKTHROUGH_URL = 'https://sessionintelligence.app/walkthrough';

export function SISidebarLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/si/login', { replace: true }); return; }

    Promise.all([isUserCoach(user.id), isUserAdmin(user.id)]).then(async ([coach, admin]) => {
      if (!coach && !admin) { navigate('/si/login', { replace: true }); return; }
      setIsAdmin(admin);

      if (coach) {
        const { data } = await supabase
          .from('coaches')
          .select('si_unlocked')
          .eq('user_id', user.id)
          .maybeSingle();
        setIsUnlocked(data?.si_unlocked === true);
      }
      setChecking(false);
    });
  }, [user, navigate]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/si/login', { replace: true });
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-brand-5 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-success-40 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-5 border-b border-gray-20 flex items-center gap-3">
        <div className="p-2 bg-success-40 rounded-xl flex-shrink-0">
          <Brain className="w-4 h-4 text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-80 leading-none">Session Intelligence<sup className="text-[0.6em]">™</sup></p>
          <p className="text-xs text-gray-40 mt-0.5 truncate">{user?.email}</p>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {NAV_ITEMS.map(item => {
          const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          const isAI = item.path === '/si/app/coach-ai';
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                active
                  ? 'bg-success-10 text-success-50'
                  : isAI
                    ? 'text-success-50 hover:bg-success-5'
                    : 'text-gray-50 hover:text-gray-80 hover:bg-gray-10'
              }`}
            >
              <item.icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-success-50' : isAI ? 'text-success-40' : 'text-gray-40'}`} />
              {item.label}
            </Link>
          );
        })}
      </nav>

      {/* Bottom actions */}
      <div className="p-3 border-t border-gray-20 space-y-1">
        {isAdmin && (
          <Link
            to="/si/admin"
            onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-50 hover:text-gray-80 hover:bg-gray-10 transition-all"
          >
            <ShieldCheck className="w-4 h-4 text-gray-40" />
            Admin Panel
          </Link>
        )}
        {isAdmin && (
          <Link
            to="/si/admin?tab=live"
            onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-50 hover:text-gray-80 hover:bg-gray-10 transition-all"
          >
            <Radio className="w-4 h-4 text-red-400" />
            Live Stream
          </Link>
        )}
        <button
          onClick={handleSignOut}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-50 hover:text-gray-80 hover:bg-gray-10 transition-all"
        >
          <LogOut className="w-4 h-4 text-gray-40" />
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-brand-5 flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 xl:w-64 flex-col bg-white border-r border-gray-20 flex-shrink-0 sticky top-0 h-screen">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="lg:hidden fixed left-0 top-0 h-full w-64 bg-white border-r border-gray-20 z-50 flex flex-col"
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-20">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-success-40 rounded-lg">
                    <Brain className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-sm font-bold text-gray-80">Session Intelligence<sup className="text-[0.6em]">™</sup></span>
                </div>
                <button onClick={() => setSidebarOpen(false)} className="p-1.5 hover:bg-gray-10 rounded-lg transition-colors">
                  <X className="w-4 h-4 text-gray-50" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <SidebarContent />
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden bg-white border-b border-gray-20 sticky top-0 z-30 px-4 h-14 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-gray-10 rounded-xl transition-colors"
          >
            <Menu className="w-5 h-5 text-gray-60" />
          </button>
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-success-40 rounded-lg">
              <Brain className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-sm font-bold text-gray-80">Session Intelligence<sup className="text-[0.6em]">™</sup></span>
          </div>
          <div className="w-9" />
        </header>

        {/* Pre-unlock top banner */}
        <AnimatePresence>
          {!isUnlocked && !bannerDismissed && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="bg-gray-80 text-white px-4 py-3"
            >
              <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold leading-snug">
                    Your Session Intelligence<sup className="text-[0.6em]">™</sup> account is ready
                  </p>
                  <p className="text-xs text-gray-30 mt-0.5 leading-relaxed">
                    Your first report will be activated during the live walkthrough. Join Patrick live to see how it works.
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <a
                    href={WALKTHROUGH_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-success-40 hover:bg-success-50 text-white rounded-xl text-xs font-semibold transition-colors"
                  >
                    Join the Live Walkthrough
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <Link
                    to="/si/app/reports"
                    className="hidden sm:inline-flex items-center gap-1 px-3 py-2 border border-gray-60 hover:border-gray-40 text-gray-30 hover:text-white rounded-xl text-xs font-medium transition-colors"
                  >
                    Preview Reports
                    <ChevronRight className="w-3 h-3" />
                  </Link>
                  <button
                    onClick={() => setBannerDismissed(true)}
                    className="p-1.5 hover:bg-gray-60 rounded-lg transition-colors"
                  >
                    <X className="w-3.5 h-3.5 text-gray-40" />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Page content */}
        <main className="flex-1">
          <Outlet context={{ isUnlocked, isAdmin }} />
        </main>
      </div>
    </div>
  );
}
